import React from 'react';
import { Button, Space, Typography } from 'antd';
import { VirtualCollection } from 'flare-ui-primitives';
import {
  EllipsisOutlined,
  FolderOpenOutlined,
  FolderOutlined,
  FormOutlined,
  InboxOutlined,
  PushpinFilled,
  PushpinOutlined,
} from '@ant-design/icons';
import { buildProjectRowMetadata } from './projectRowMetadata.js';
import SidebarActionPopover from './SidebarActionPopover.jsx';
import SessionRows from './SessionRows.jsx';
import { getProjectId } from './sessionIdentity.js';

const { Text } = Typography;
const conditionalValue = (condition, whenTrue, whenFalse = null) => (
  condition ? whenTrue : whenFalse
);
const firstTruthy = (...values) => values.find(Boolean);
const projectArchived = project => String(project.status ?? '').trim() === 'archived';
const ProjectFolderIcon = ({ projectRecord, expanded }) => (
  firstTruthy(projectRecord.icon, expanded ? <FolderOpenOutlined /> : <FolderOutlined />)
);
const selectionErrorFor = (navigation, projectId) => (
  navigation?.projectId === projectId ? navigation.error : null
);

const ProjectRowActions = ({ onArchiveProject, onPinProject, project, themeTokens }) => {
  const [open, setOpen] = React.useState(false);
  const [archiveConfirming, setArchiveConfirming] = React.useState(false);
  const close = () => {
    setOpen(false);
    setArchiveConfirming(false);
  };
  const handleOpenChange = (nextOpen) => {
    setOpen(nextOpen);
    if (!nextOpen) setArchiveConfirming(false);
  };
  const handlePin = async () => {
    await onPinProject?.(project);
    close();
  };
  const handleArchive = async () => {
    await onArchiveProject?.(project);
    close();
  };
  const pinned = project?.user_pinned === true;
  const content = (
    <Space className="flare-project-row-actions" orientation="vertical" size={0}>
      {typeof onPinProject === 'function' ? (
        <Button
          block
          icon={pinned ? <PushpinFilled /> : <PushpinOutlined />}
          onClick={handlePin}
          type="text"
        >
          {pinned ? '取消置顶项目' : '置顶项目'}
        </Button>
      ) : null}
      {typeof onArchiveProject === 'function' ? (
        <>
          <div aria-hidden="true" className="flare-project-row-actions-divider" />
          <Button
            block
            danger
            icon={<InboxOutlined />}
            onClick={archiveConfirming ? handleArchive : () => setArchiveConfirming(true)}
            type="text"
          >
            {archiveConfirming ? '再次点击确认归档' : '归档项目'}
          </Button>
        </>
      ) : null}
    </Space>
  );
  return (
    <span onClick={(event) => event.stopPropagation()}>
      <SidebarActionPopover
        arrow={false}
        classNames={{ root: 'flare-project-row-actions-popover' }}
        content={content}
        onOpenChange={handleOpenChange}
        open={open}
        placement="rightTop"
        themeTokens={themeTokens}
        trigger="click"
      >
        <Button
          aria-label="项目操作"
          icon={<EllipsisOutlined style={{ fontSize: 18 }} />}
          size="small"
          style={{ color: themeTokens.textSecondary }}
          title="更多"
          type="text"
        />
      </SidebarActionPopover>
    </span>
  );
};

const ProjectSessionList = ({ projectId, expanded, sidebarNavigation, ...props }) => {
  const pinSession = props.onPinSession;
  const handlePinSession = React.useCallback(async (...args) => {
    await pinSession?.(...args);
    await sidebarNavigation.refresh(projectId);
  }, [pinSession, projectId, sidebarNavigation]);
  const onPinSession = typeof pinSession === 'function' ? handlePinSession : undefined;
  const projection = sidebarNavigation?.projection[projectId];
  if (!expanded || !projection) return null;
  const selectionError = selectionErrorFor(sidebarNavigation.navigation, projectId);
  return (
    <div className="flare-project-session-rail" style={{ marginLeft: 20, paddingLeft: 10 }}>
      {conditionalValue(Boolean(projection.error), (
        <div role="alert">
          <Text type="danger">{projection.error?.message}</Text>
          <Button size="small" type="text" onClick={() => sidebarNavigation.refresh(projectId)}>重试</Button>
        </div>
      ))}
      {conditionalValue(Boolean(selectionError), <Text type="danger" role="alert">{selectionError?.message}</Text>)}
      {projection.canRenderSessions === true ? <SessionRows
        {...props}
        nested showGroups={false} emptySessionsLabel="暂无会话"
        sessions={projection.items} loading={projection.loading} loadingMore={projection.loadingMore}
        hasMore={projection.window.hasMore}
        continuationError={projection.error}
        continuationKey={String(projection.window.nextCursor ?? '')}
        scopeKey={`sidebar-sessions:${projectId}`}
        onLoadMore={() => sidebarNavigation.loadMore(projectId)}
        onSelectSession={sessionId => props.onSelectSession?.(sessionId, projectId)}
        onRenameSession={async (...args) => { await props.onRenameSession?.(...args); await sidebarNavigation.refresh(projectId); }}
        onArchiveSession={async (...args) => { await props.onArchiveSession?.(...args); await sidebarNavigation.refresh(projectId); }}
        onPinSession={onPinSession}
      /> : null}
    </div>
  );
};

const ProjectRowItem = ({
  activeProjectId, createSessionInProjectLabel, onArchiveProject, onCreateSession, onPinProject, onProjectSelect,
  project, rowProps, sidebarNavigation, themeTokens, paneCompact,
}) => {
  const projectRecord = Object.assign({}, project);
  const projectId = getProjectId(project);
  const projectMetadata = buildProjectRowMetadata(projectRecord);
  const active = [projectId, projectId === activeProjectId].every(Boolean);
  const archived = projectArchived(projectRecord);
  const expanded = sidebarNavigation?.expandedIds.includes(projectId) === true;
  const handleProjectSelect = selectedProject => {
    if (sidebarNavigation?.dispatch('click', project)) onProjectSelect?.(selectedProject);
  };
  return (
    <div>
      <div
        data-project-navigation-row={projectId}
        aria-expanded={expanded}
        aria-current={conditionalValue(active, 'page', undefined)}
        className={[
          'flare-project-item flare-project-row',
          conditionalValue(active, 'is-active', ''),
          conditionalValue(projectRecord.user_pinned === true, 'is-pinned', ''),
        ].filter(Boolean).join(' ')}
        onClick={() => handleProjectSelect(project)}
        onKeyDown={(event) => {
          if (event.currentTarget !== event.target || !new Set(['Enter', ' ']).has(event.key)) return;
          event.preventDefault();
          handleProjectSelect(project);
        }}
        role="button"
        tabIndex={0}
        style={{
          alignItems: 'center', border: '1px solid transparent',
          borderRadius: 10, color: themeTokens.textPrimary, cursor: 'pointer', display: 'grid',
          gap: 8, gridTemplateColumns: '24px minmax(0,1fr) auto', overflow: 'hidden',
          padding: paneCompact ? '7px 6px' : '8px 8px', textAlign: 'left', width: '100%',
        }}
      >
        <span className="flare-project-row-icon">
          <ProjectFolderIcon projectRecord={projectRecord} expanded={expanded} />
        </span>
        <Space className="flare-project-row-copy" orientation="vertical" size={2}>
          <Text className="flare-project-row-title" strong ellipsis>
            {firstTruthy(projectRecord.name, '未命名项目')}
          </Text>
          {conditionalValue(Boolean(projectMetadata), (
            <Text className="flare-project-row-meta" style={{ color: themeTokens.textMuted }} type="secondary">
              {projectMetadata}
            </Text>
          ))}
        </Space>
        {conditionalValue((active || expanded) && !archived, (
          <Space size={0}>
            {conditionalValue(typeof onCreateSession === 'function', (
              <Button
                aria-label={createSessionInProjectLabel}
                icon={<FormOutlined />}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  sidebarNavigation?.reset();
                  onCreateSession(project);
                }}
                size="small"
                style={{ color: themeTokens.textSecondary }}
                title={createSessionInProjectLabel}
                type="text"
              />
            ))}
            <ProjectRowActions
              onArchiveProject={onArchiveProject}
              onPinProject={onPinProject}
              project={project}
              themeTokens={themeTokens}
            />
          </Space>
        ))}
      </div>
      <ProjectSessionList
        {...rowProps}
        projectId={projectId}
        expanded={expanded}
        sidebarNavigation={sidebarNavigation}
      />
    </div>
  );
};

/** Render prepared sidebar browsing independently of workspace navigation. */
export default function ProjectRows(props) {
  const {
    paneCompact,
    themeTokens,
    projectItems,
    activeProjectId,
    createSessionInProjectLabel,
    onCreateSession,
    onArchiveProject,
    onPinProject,
    onProjectSelect,
    sidebarNavigation, activeSessionId, loadingSessionId, runningSessionIds,
    onSelectSession, onRenameSession, onArchiveSession,
    onPinSession,
    projectLoadingMore,
    projectHasMore,
    projectContinuationKey,
    collectionScopeKey,
    projectError,
    onLoadMoreProjects,
    scrollElement,
  } = {
    paneCompact: false,
    projectItems: [],
    activeProjectId: '',
    createSessionInProjectLabel: '新会话',
    projectLoadingMore: false,
    projectHasMore: false,
    ...props,
  };
  const resolvedCreateSessionLabel = firstTruthy(createSessionInProjectLabel, '新会话');
  const canonicalProjectItems = React.useMemo(
    () => (Array.isArray(projectItems) ? projectItems : []),
    [projectItems],
  );

  if (canonicalProjectItems.length === 0) return null;
  const rowProps = {
    activeSessionId, loadingSessionId, onArchiveSession, onPinSession,
    onRenameSession, onSelectSession, paneCompact, runningSessionIds,
    scrollElement, themeTokens,
  };
  const renderProject = project => (
    <ProjectRowItem
      activeProjectId={activeProjectId}
      createSessionInProjectLabel={resolvedCreateSessionLabel}
      onArchiveProject={onArchiveProject}
      onCreateSession={onCreateSession}
      onPinProject={onPinProject}
      onProjectSelect={onProjectSelect}
      paneCompact={paneCompact}
      project={project}
      rowProps={rowProps}
      sidebarNavigation={sidebarNavigation}
      themeTokens={themeTokens}
    />
  );
  if (!scrollElement) {
    const acceptedProject = canonicalProjectItems.find(project => getProjectId(project) === activeProjectId)
      ?? canonicalProjectItems[0];
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {renderProject(acceptedProject)}
      </div>
    );
  }

  return (
    <VirtualCollection
      ariaLabel="项目列表"
      className="flare-sidebar-project-virtual-collection"
      continuation={{
        error: projectError ?? null,
        hasMore: projectHasMore,
        key: String(projectContinuationKey ?? ''),
        loading: projectLoadingMore,
      }}
      getItemKey={(project) => firstTruthy(getProjectId(project), project.name)}
      items={canonicalProjectItems}
      layout={{ columns: 1, endThreshold: 96, estimateSize: 62, gap: 8, overscan: 3 }}
      onLoadMore={() => onLoadMoreProjects?.()}
      renderItem={renderProject}
      scopeKey={`sidebar-projects:${collectionScopeKey}`}
      scrollElement={scrollElement}
    />
  );
}
